import { alphaAdminUi } from "../../components/admin/alphaAdminUi";
import {
    AlertTriangle,
    BarChart3,
    Bell,
    Calendar,
    CheckCircle,
    ChevronDown,
    Clock,
    Copy,
    Download,
    Edit3,
    ExternalLink,
    HelpCircle,
    Plus,
    RefreshCw,
    Search,
    ShieldOff,
    Trash2,
    Upload,
    Users2,
    X,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AdminAccountSummary from "../../components/AdminAccountSummary";
import AdminPageLayout from "../../components/AdminPageLayout";
import AccountDeletionDialog from "../../components/AccountDeletionDialog";
import accountDeletionService from "../../services/accountDeletionService";
import ContractExclusionsModal from "../../components/ContractExclusionsModal";
import ResponsiveActionButton from "../../components/ResponsiveActionButton";
import SelectableTableRow from "../../components/SelectableTableRow";
import SplitScreenLayout from "../../components/SplitScreenLayout";
import UserConfigurationModal from "../../components/UserConfigurationModal";
import UserModal from "../../components/UserModal";
import UserPermissionsModal from "../../components/UserPermissionsModal";
import AlphaSettingsModal from "../alpha/AlphaSettingsModal";
import advisorAccountService, { AdvisorAccount } from "../../services/advisorAccountService";
import BulkAddUsersModal from "../../components/admin/BulkAddUsersModal";
import UserAgreementsCard from "../../components/admin/UserAgreementsCard";
import UserInvestorStrategyCard from "../../components/admin/UserInvestorStrategyCard";
import UserIbkrConfigurationCard from "../../components/admin/UserIbkrConfigurationCard";
import UserNotificationContactsCard from "../../components/admin/UserNotificationContactsCard";
import UserProductNotificationMappingsModal from "../../components/admin/UserProductNotificationMappingsModal";
import { useProductUpdateNotificationsEnabled } from "../../hooks/useProductUpdateNotificationsEnabled";
import { useTheme } from "../../contexts/ThemeContext";
import { useToast } from "../../contexts/ToastContext";
import { useAuth } from "../../contexts/AuthContext";
import { useUsers } from "../../hooks/useUsers";
import { useUserAgreements } from "../../hooks/useUserAgreements";
import { useUserStrategyRecords } from "../../hooks/useUserStrategyRecords";
import userConfigurationService, { UserConfiguration } from "../../services/UserConfigurationService";
import userConfigurationSchemaService, {
  UserConfigurationSchemaDescriptor,
} from "../../services/userConfigurationSchemaService";
import formService from "../../services/formService";
import httpService from "../../services/httpService";
import userContractExclusionsService, { UserContractExclusion } from "../../services/userContractExclusionsService";
import usersService, { User } from "../../services/usersService";
import type { AdminFormResponse, FormQuestion } from "../../types/form";
import { isValidPlutusUserId } from "../../utils/plutusUserId";
import { isCustomerViewEligible } from "../../hooks/useAlphaAccess";
import { createAdminCustomerViewRouteMapping } from "../../utils/adminCustomerViewRouteSession";
import { emitAdminCustomerViewOwnerMismatch } from "./adminCustomerViewDiagnostics";
import UserActivityPanel from "./components/UserActivityPanel";
import DeletedUsersTab from "./DeletedUsersPage";

const isNotFoundError = (err: any): boolean => (err.response?.status || err.status) === 404;

const AllUsersTab: React.FC = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const toast = useToast();
  const { user: actor, logout } = useAuth();
  const productNotificationsEnabled = useProductUpdateNotificationsEnabled();
  const [productNotificationsUser, setProductNotificationsUser] = useState<User | null>(null);
  useEffect(() => {
    if (!productNotificationsEnabled) setProductNotificationsUser(null);
  }, [productNotificationsEnabled]);

  // Use the new useUsers hook for data fetching
  const {
    users,
    loading,
    hasLoaded,
    error,
    pagination,
    sorting,
    search,
    filters,
    actions: { fetchUsers: userFetchUsers },
  } = useUsers({ pageSize: 10 });
  
  // Alias for backward compatibility with existing code
  const userActions = useMemo(() => ({ fetchUsers: userFetchUsers }), [userFetchUsers]);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [permissionsUser, setPermissionsUser] = useState<User | null>(null);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [configUserId, setConfigUserId] = useState<string | null>(null);
  const [configUserName, setConfigUserName] = useState<string | null>(null);
  const [exclusionsModalOpen, setExclusionsModalOpen] = useState(false);
  const [exclusionsUserId, setExclusionsUserId] = useState<string | null>(null);
  const [exclusionsUserName, setExclusionsUserName] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [bulkAddModalOpen, setBulkAddModalOpen] = useState(false);

  const [sidebarUser, setSidebarUser] = useState<User | null>(null);
  const [downloading, setDownloading] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const selectedSidebarUserIdRef = useRef<string | null>(null);
  selectedSidebarUserIdRef.current = sidebarUser?.id || null;

  // Non-lowercase email bulk delete state
  const [nonLowercaseModalOpen, setNonLowercaseModalOpen] = useState(false);
  const [nonLowercaseUsers, setNonLowercaseUsers] = useState<User[]>([]);
  const [scanningNonLowercase, setScanningNonLowercase] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState<{ completed: number; total: number; failed: string[] }>({ completed: 0, total: 0, failed: [] });

  const bulkRequestRef = useRef(0);
  useEffect(() => () => { ++bulkRequestRef.current; }, [actor?.id]);

  // Role filter dropdown state
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const roleDropdownRef = useRef<HTMLDivElement>(null);
  const [configurationSchema, setConfigurationSchema] = useState<UserConfigurationSchemaDescriptor | null>(null);
  const [configurationSchemaLoading, setConfigurationSchemaLoading] = useState(false);

  // Click-outside handler for role dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target as Node)) {
        setRoleDropdownOpen(false);
      }
    };

    if (roleDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [roleDropdownOpen]);

  useEffect(() => {
    let cancelled = false;
    const loadConfigurationSchema = async () => {
      try {
        setConfigurationSchemaLoading(true);
        const schema = await userConfigurationSchemaService.getActiveSchema();
        if (!cancelled) {
          setConfigurationSchema(schema.descriptor);
        }
      } catch (err: any) {
        if (!cancelled && !isNotFoundError(err)) {
          console.warn("Failed to fetch user configuration schema:", err);
        }
        if (!cancelled) {
          setConfigurationSchema(null);
        }
      } finally {
        if (!cancelled) {
          setConfigurationSchemaLoading(false);
        }
      }
    };

    loadConfigurationSchema();
    return () => {
      cancelled = true;
    };
  }, []);

  // Ref to track which user ID we're currently fetching sidebar data for
  // Used to prevent race conditions when clicking users quickly
  const currentSidebarUserIdRef = useRef<string | null>(null);
  const formQuestionsCacheRef = useRef<FormQuestion[] | null>(null);

  // Sidebar user detail state
  const [sidebarUserConfig, setSidebarUserConfig] = useState<UserConfiguration | null>(null);
  const [sidebarConfigLoading, setSidebarConfigLoading] = useState(false);
  const [sidebarExclusions, setSidebarExclusions] = useState<UserContractExclusion[]>([]);
  const [sidebarExclusionsLoading, setSidebarExclusionsLoading] = useState(false);
  const {
    agreements: sidebarAgreements,
    isLoading: sidebarAgreementsLoading,
  } = useUserAgreements(sidebarUser?.id ?? null);
  const {
    strategyRecords: sidebarStrategies,
    managedCount: sidebarManagedCount,
    isLoading: sidebarStrategiesLoading,
    hasError: sidebarStrategiesError,
  } = useUserStrategyRecords(sidebarUser?.id ?? null);
  const [sidebarFormQuestions, setSidebarFormQuestions] = useState<FormQuestion[] | null>(null);
  const [sidebarFormResponses, setSidebarFormResponses] = useState<AdminFormResponse[]>([]);
  const [sidebarFormResponsesLoading, setSidebarFormResponsesLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(false);

  useEffect(() => {
    if (
      sidebarUser
      && sidebarUserConfig
      && sidebarUserConfig.userId !== sidebarUser.id
    ) {
      emitAdminCustomerViewOwnerMismatch("configuration");
    }
  }, [sidebarUser, sidebarUserConfig]);

  // Advisor account link state
  const [advisorAccountInput, setAdvisorAccountInput] = useState("");
  const [advisorAccountLinked, setAdvisorAccountLinked] = useState<AdvisorAccount | null>(null);
  const [advisorAccountLoading, setAdvisorAccountLoading] = useState(false);
  const [advisorAccountError, setAdvisorAccountError] = useState<string | null>(null);
  const [advisorLinkLoading, setAdvisorLinkLoading] = useState(false);
  const advisorMutationRef = useRef(0);

  // Fetch sidebar user additional data when a user is selected.
  // Uses currentSidebarUserIdRef to prevent stale responses from overwriting newer data.
  // All loading flags are set upfront so every section shows a spinner immediately on user switch.
  const fetchSidebarUserData = useCallback(async (userId: string) => {
    // Mark this userId as the current one we're fetching for
    currentSidebarUserIdRef.current = userId;

    // Helper to check if we're still fetching for the same user
    const isStillCurrent = () => currentSidebarUserIdRef.current === userId;

    // Set ALL loading flags immediately so every section shows a spinner from T0
    setSidebarConfigLoading(true);
    setSidebarExclusionsLoading(true);
    setSidebarFormResponsesLoading(true);

    // Fetch user configuration
    try {
      const config = await userConfigurationService.getUserConfiguration(userId);
      if (isStillCurrent()) {
        setSidebarUserConfig(config);
      }
    } catch (err: any) {
      if (isStillCurrent()) {
        const status = err.response?.status || err.status;
        if (status === 404) {
          setSidebarUserConfig(null);
        } else {
          console.error("Failed to fetch user config:", err);
          setSidebarUserConfig(null);
        }
      }
    } finally {
      if (isStillCurrent()) {
        setSidebarConfigLoading(false);
      }
    }

    // Bail if user changed during config fetch
    if (!isStillCurrent()) return;

    // Fetch contract exclusions
    try {
      const data = await userContractExclusionsService.getExclusionsForUser(userId);
      if (isStillCurrent()) {
        setSidebarExclusions(data.exclusions || []);
      }
    } catch (err: any) {
      if (isStillCurrent()) {
        const status = err.response?.status || err.status;
        if (status === 404 || status === 400) {
          setSidebarExclusions([]);
        } else {
          console.error("Failed to fetch exclusions:", err);
          setSidebarExclusions([]);
        }
      }
    } finally {
      if (isStillCurrent()) {
        setSidebarExclusionsLoading(false);
      }
    }

    if (!isStillCurrent()) return;

    // Fetch form questions (cached in ref) and this user's Q&A responses via GET /v2/form/response/user/:userId
    const loadFormData = async () => {
      try {
        const questionsPromise = formQuestionsCacheRef.current
          ? Promise.resolve(formQuestionsCacheRef.current)
          : formService.listQuestions().then(q => {
              formQuestionsCacheRef.current = q;
              return q;
            });
        const [questions, responses] = await Promise.all([
          questionsPromise,
          formService.getResponsesForUser(userId),
        ]);
        if (!isStillCurrent()) return;
        setSidebarFormQuestions(questions);
        setSidebarFormResponses(responses);
      } catch (err) {
        if (isStillCurrent()) {
          console.error("Failed to fetch form Q&A:", err);
          setSidebarFormResponses([]);
        }
      } finally {
        if (isStillCurrent()) {
          setSidebarFormResponsesLoading(false);
        }
      }
    };
    loadFormData();
  }, []);

  // Prepare sidebar state before switching users so stale content never renders for the new selection.
  const prepareSidebarForUserSwitch = useCallback(() => {
    setSidebarUserConfig(null);
    setSidebarExclusions([]);
    setSidebarFormResponses([]);
    setSidebarConfigLoading(true);
    setSidebarExclusionsLoading(true);
    setSidebarFormResponsesLoading(true);
    // Reset advisor state
    setAdvisorAccountLinked(null);
    setAdvisorAccountInput("");
    setAdvisorAccountError(null);
    ++advisorMutationRef.current;
    setAdvisorLinkLoading(false);
  }, []);

  // Fetch the advisor account linked to a user (by checking all accounts)
  const fetchLinkedAdvisorAccount = useCallback(async (userId: string) => {
    setAdvisorAccountLoading(true);
    setAdvisorAccountLinked(null);
    setAdvisorAccountError(null);
    try {
      const accounts = await advisorAccountService.getAdvisorAccounts();
      if (currentSidebarUserIdRef.current !== userId) return;
      const linked = accounts.find(a => a.userId === userId) || null;
      setAdvisorAccountLinked(linked);
      if (linked) {
        setAdvisorAccountInput(linked.accountId);
      }
    } catch {
      // Non-critical; advisor might not have an account yet
    } finally {
      if (currentSidebarUserIdRef.current === userId) {
        setAdvisorAccountLoading(false);
      }
    }
  }, []);

  // Link an advisor account to the current sidebar user
  const handleLinkAdvisorAccount = async () => {
    if (!sidebarUser || !advisorAccountInput.trim()) return;
    const targetUserId = sidebarUser.id;
    const requestId = ++advisorMutationRef.current;
    const isCurrent = () => currentSidebarUserIdRef.current === targetUserId && advisorMutationRef.current === requestId;
    setAdvisorLinkLoading(true);
    setAdvisorAccountError(null);
    try {
      // Step 1: resolve IBKR account ID to the DB row
      const advisorAccount = await advisorAccountService.getAdvisorAccountById(advisorAccountInput.trim());
      if (!isCurrent()) return;
      // Step 2: patch the userId onto that row
      const updated = await advisorAccountService.updateAdvisorAccount(advisorAccount.id, {
        userId: sidebarUser.id,
      });
      if (!isCurrent()) return;
      setAdvisorAccountLinked(updated);
      setAdvisorAccountError(null);
    } catch (err: any) {
      if (!isCurrent()) return;
      const status = err?.response?.status;
      if (status === 404) {
        setAdvisorAccountError(`Advisor account "${advisorAccountInput.trim()}" not found.`);
      } else {
        const message = err?.response?.data?.error || err?.message || "Failed to link advisor account";
        setAdvisorAccountError(message);
      }
    } finally {
      if (isCurrent()) setAdvisorLinkLoading(false);
    }
  };

  // Unlink an advisor account from the current sidebar user
  const handleUnlinkAdvisorAccount = async () => {
    if (!sidebarUser || !advisorAccountLinked) return;
    const targetUserId = sidebarUser.id;
    const requestId = ++advisorMutationRef.current;
    const isCurrent = () => currentSidebarUserIdRef.current === targetUserId && advisorMutationRef.current === requestId;
    setAdvisorLinkLoading(true);
    setAdvisorAccountError(null);
    try {
      await advisorAccountService.updateAdvisorAccount(advisorAccountLinked.id, { userId: null });
      if (!isCurrent()) return;
      setAdvisorAccountLinked(null);
      setAdvisorAccountInput("");
    } catch (err: any) {
      if (!isCurrent()) return;
      const message = err?.response?.data?.error || err?.message || "Failed to unlink advisor account";
      setAdvisorAccountError(message);
    } finally {
      if (isCurrent()) setAdvisorLinkLoading(false);
    }
  };

  const handleSidebarUserSelect = useCallback((user: User) => {
    // Avoid no-op reselects that could leave forced loading states without triggering a fetch effect.
    if (selectedSidebarUserIdRef.current === user.id) return;
    // Invalidate prior in-flight sidebar chains immediately on click (before the fetch effect runs).
    currentSidebarUserIdRef.current = user.id;
    prepareSidebarForUserSwitch();
    setSidebarUser(user);
  }, [prepareSidebarForUserSwitch]);

  useEffect(() => {
    if (sidebarUser) {
      // Fetch new user's data. Selection handler already prepared empty/loading sidebar state.
      fetchSidebarUserData(sidebarUser.id);
      // If user has ADVISOR role, fetch their linked advisor account
      if (sidebarUser.roles?.includes("ADVISOR")) {
        fetchLinkedAdvisorAccount(sidebarUser.id);
      }
    } else {
      // Reset sidebar data when closed
      // Also clear the ref so any in-flight requests are ignored
      currentSidebarUserIdRef.current = null;
      setSidebarUserConfig(null);
      setSidebarExclusions([]);
      setSidebarFormResponses([]);
      setSidebarFormResponsesLoading(false);
      // Clear loading states to avoid stuck spinners
      setSidebarConfigLoading(false);
      setSidebarExclusionsLoading(false);
    }
  }, [sidebarUser, fetchSidebarUserData, fetchLinkedAdvisorAccount]);

  const handleCreateUser = async (userData: Partial<User> & { roles?: string[]; providerId?: string }) => {
    const response = await httpService.postAuth<{ user: User }>("/register", {
      email: userData.email,
      name: userData.name,
      roles: userData.roles || ["INVESTOR"],
    });

    const result = response.data;

    if (userData.roles?.includes("PROVIDER") && userData.providerId && result.user?.id) {
      try {
        await httpService.post(`/providers/${userData.providerId}/users`, {
          userId: result.user.id,
        });
      } catch (err) {
        console.error("Failed to assign user to provider:", err);
        throw new Error(`Failed to assign user to provider: ${err instanceof Error ? err.message : "Unknown error"}`);
      }
    }

    await userActions.fetchUsers();
  };

  const saveUser = async (targetUser: User, userData: Partial<User> & { roles?: string[]; providerId?: string }) => {

    const updated = await usersService.updateUser(targetUser.id, {
      ...(userData.name !== undefined ? { name: userData.name } : {}),
      ...(userData.emailVerified !== undefined ? { emailVerified: userData.emailVerified } : {}),
      ...(userData.roles !== undefined ? { roles: userData.roles } : {}),
      ...(userData.scopes !== undefined ? { scopes: userData.scopes } : {}),
    });

    if ((userData.roles ?? targetUser.roles)?.includes("PROVIDER") && userData.providerId !== undefined && userData.providerId) {
      try {
        if (targetUser.provider) {
          try {
            await httpService.delete(`/providers/${targetUser.provider.id}/users/${targetUser.id}`);
          } catch (err) {
            console.warn("Failed to remove existing provider assignment:", err);
          }
        }
        try {
          await httpService.post(`/providers/${userData.providerId}/users`, {
            userId: targetUser.id,
          });
        } catch (err) {
          console.warn("Failed to assign user to provider:", err);
        }
      } catch (err) {
        console.warn("Failed to assign user to provider:", err);
      }
    } else if (userData.roles !== undefined && !userData.roles.includes("PROVIDER") && targetUser.provider) {
      try {
        await httpService.delete(`/providers/${targetUser.provider.id}/users/${targetUser.id}`);
      } catch (err) {
        console.warn("Failed to remove provider assignment:", err);
      }
    }

    setSidebarUser(current => current?.id === updated.id ? { ...current, ...updated } : current);
    await userActions.fetchUsers();
    toast.success("User saved.");
  };

  const openDeleteConfirmation = (user: User) => {
    setUserToDelete(user);
    setDeleteConfirmOpen(true);
  };

  const openCreateModal = () => {
    setSelectedUser(null);
    setModalMode("create");
    setModalOpen(true);
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setModalMode("edit");
    setModalOpen(true);
  };

  const openConfigModal = (user: User) => {
    setConfigUserId(user.id);
    setConfigUserName(user.name || user.email);
    setConfigModalOpen(true);
  };

  const openExclusionsModal = (user: User) => {
    setExclusionsUserId(user.id);
    setExclusionsUserName(user.name || user.email);
    setExclusionsModalOpen(true);
  };

  const downloadCSV = async () => {
    try {
      setDownloading(true);

      interface CsvRow {
        id: string;
        name: string;
        email: string;
        roles: string;
        provider: string;
        emailVerified: string;
        createdAt: string;
        updatedAt: string;
      }

      const allRows: CsvRow[] = [];
      let currentPage = 1;
      let totalPages = 1;

      // Fetch all pages, one at a time
      do {
        const response = await usersService.findUsers({
          pageNumber: currentPage,
          pageSize: 100, // Larger page size for efficiency
          orderByColumn: sorting.sortColumn,
          orderByDirection: sorting.sortDirection,
          searchString: search.searchQuery || undefined,
          searchColumn: search.searchColumn || undefined,
          roles: filters.roleFilters.size > 0
            ? (Array.from(filters.roleFilters) as ("ADMIN" | "INVESTOR" | "PROVIDER" | "ADVISOR")[])
            : undefined,
        });

        totalPages = response.totalPages;

        // Add rows from this page
        response.items.forEach(u => {
          allRows.push({
            id: u.id,
            name: u.name || "",
            email: u.email,
            roles: (u.roles || []).join(";"),
            provider: u.provider?.name || "",
            emailVerified: u.emailVerified ? "true" : "false",
            createdAt: new Date(u.createdAt).toISOString(),
            updatedAt: new Date(u.updatedAt).toISOString(),
          });
        });

        currentPage++;
      } while (currentPage <= totalPages);

      if (allRows.length === 0) {
        return;
      }

      // Generate CSV
      const headers = Object.keys(allRows[0]);
      const csv = [
        headers.join(","),
        ...allRows.map(r =>
          headers
            .map(h => {
              const cell = (r as unknown as Record<string, string>)[h] ?? "";
              const str = String(cell);
              return /[",\n]/.test(str) ? '"' + str.replace(/"/g, '""') + '"' : str;
            })
            .join(",")
        ),
      ].join("\n");

      // Download file
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `users_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to download CSV";
      toast.error(message, 0);
    } finally {
      setDownloading(false);
    }
  };

  const handleCopyUserId = (userId: string) => {
    navigator.clipboard.writeText(userId);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  const handleScanNonLowercaseEmails = useCallback(async () => {
    const requestId = ++bulkRequestRef.current;
    try {
      setScanningNonLowercase(true);
      setNonLowercaseUsers([]);

      const foundUsers: User[] = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const response = await usersService.findUsers({
          pageNumber: currentPage,
          pageSize: 100,
        });
        if (requestId !== bulkRequestRef.current) return;
        totalPages = response.totalPages;

        response.items.forEach((u) => {
          if (u.id !== actor?.id && u.email !== u.email.toLowerCase()) {
            foundUsers.push(u);
          }
        });

        currentPage++;
      } while (currentPage <= totalPages);

      setNonLowercaseUsers(foundUsers);
      setNonLowercaseModalOpen(true);

      if (foundUsers.length === 0) {
        toast.success("All user emails are already lowercase.");
      }
    } catch (err) {
      if (requestId !== bulkRequestRef.current) return;
      const message = err instanceof Error ? err.message : "Failed to scan users";
      toast.error(message, 0);
    } finally {
      if (requestId === bulkRequestRef.current) setScanningNonLowercase(false);
    }
  }, [toast, actor?.id]);

  const handleBulkDeleteNonLowercase = useCallback(async () => {
    if (nonLowercaseUsers.length === 0) return;

    const requestId = ++bulkRequestRef.current;
    const total = nonLowercaseUsers.length;
    setBulkDeleting(true);
    setBulkDeleteProgress({ completed: 0, total, failed: [] });

    const failed: string[] = [];

    for (let i = 0; i < nonLowercaseUsers.length; i++) {
      if (requestId !== bulkRequestRef.current) return;
      const user = nonLowercaseUsers[i];
      try {
        if (user.id === actor?.id) throw new Error("You cannot delete your own admin account.");
        const eligibility = await accountDeletionService.getAdminEligibility(user.id);
        if (requestId !== bulkRequestRef.current) return;
        if (!eligibility.eligible) throw new Error("Account deletion is blocked.");
        const result = await accountDeletionService.deleteUser(user.id);
        if (requestId !== bulkRequestRef.current) return;
        if (result.logoutActor) {
          setBulkDeleting(false);
          setNonLowercaseModalOpen(false);
          await logout();
          return;
        }
      } catch (err) {
        if (requestId !== bulkRequestRef.current) return;
        failed.push(user.email);
      }
      setBulkDeleteProgress({ completed: i + 1, total, failed: [...failed] });
    }

    setBulkDeleting(false);

    const successCount = total - failed.length;
    if (failed.length === 0) {
      toast.success(`Successfully deleted ${successCount} user(s) with non-lowercase emails.`);
    } else {
      toast.error(`Deleted ${successCount} of ${total}. Failed: ${failed.join(", ")}`, 0);
    }

    setNonLowercaseModalOpen(false);
    setNonLowercaseUsers([]);
    setBulkDeleteProgress({ completed: 0, total: 0, failed: [] });
    await userActions.fetchUsers();
  }, [nonLowercaseUsers, toast, userActions, actor?.id, logout]);

  const getSearchPlaceholder = () => {
    switch (search.searchColumn) {
      case "id": return "Search by user ID...";
      case "name": return "Search by name...";
      case "email": return "Search by email...";
      case "providerName": return "Search by provider name...";
      default: return "Search all columns...";
    }
  };

  const getRoleBadgeColor = (role: string | null | undefined) => {
    return role === "ADMIN" ? "border border-v7-bronze/25 bg-v7-bronze/10 text-v7-bronze-dark"
      : "border border-v7-ink/15 bg-v7-paper text-v7-ink-soft";
  };

  const sortIndicator = (key: string) => {
    if (sorting.sortColumn !== key) return null;
    return sorting.sortDirection === "asc" ? " ▲" : " ▼";
  };

  // Pagination values
  const startIndex = (pagination.currentPage - 1) * pagination.pageSize + 1;
  const endIndex = Math.min(pagination.currentPage * pagination.pageSize, pagination.totalItems);

  if (loading && users.length === 0 && !hasLoaded) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div
            className={`animate-spin rounded-full h-12 w-12 border-b-2 border-v7-ink/25 mx-auto`}
          ></div>
          <p className={`mt-4 text-v7-ink-soft`}>Loading users...</p>
        </div>
      </div>
    );
  }

  if (error && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className={`text-xl mb-4 text-v7-rust`}>Error</div>
          <p className="text-v7-ink-soft">{error}</p>
          <button
            onClick={() => userActions.fetchUsers()}
            className={`mt-4 px-4 py-2 rounded-v7-control transition-colors bg-v7-ink text-v7-white hover:bg-v7-ink`}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100dvh-270px)] min-h-[420px] flex-col">
      <div className="mb-5 flex flex-shrink-0 flex-wrap items-center justify-end gap-2">
          <ResponsiveActionButton appearance="alpha"
            label="Refresh"
            icon={<RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />}
            onClick={() => userActions.fetchUsers()}
            disabled={loading}
            variant="default"
          />
          <ResponsiveActionButton appearance="alpha"
            label={scanningNonLowercase ? "Scanning..." : "Non-Lowercase Emails"}
            icon={<AlertTriangle className={`w-4 h-4 ${scanningNonLowercase ? "animate-pulse" : ""}`} />}
            onClick={handleScanNonLowercaseEmails}
            disabled={scanningNonLowercase || loading}
            variant="danger"
            title="Find and delete users with non-lowercase email addresses"
          />
          <ResponsiveActionButton appearance="alpha"
            label="Roster Sync"
            icon={<Upload className="w-4 h-4" />}
            onClick={() => setBulkAddModalOpen(true)}
            variant="default"
          />
          <ResponsiveActionButton appearance="alpha"
            label="Create User"
            icon={<Plus className="w-4 h-4" />}
            onClick={openCreateModal}
            variant="primary"
          />
      </div>
      <div className="min-h-0 flex-1">
          <SplitScreenLayout appearance="alpha"
            isSidebarOpen={!!sidebarUser}
            sidebarPosition="right"
            mainContentMinWidth={0}
            sidebarWidth="50%"
            sidebarMaxWidth="60%"
            onCloseSidebar={() => setSidebarUser(null)}
            sidebarContent={
              sidebarUser ? (
                <div className="flex h-full flex-col bg-v7-white p-4 md:p-6">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4 flex-shrink-0">
                    <h2 className={`text-xl font-bold text-v7-ink`}>
                      User Details
                    </h2>
                    <button
                      onClick={() => setSidebarUser(null)}
                      aria-label="Close user details"
                      className={`p-2 rounded-full hover:bg-v7-mist text-v7-ink/60`}
                    >
                      <X className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>

                  {/* Scrollable Content */}
                  {/* ph-mask: redacts names, emails, provider IDs, and any
                      other user PII from PostHog session replays. Admins
                      themselves still see real data; only the recording is
                      masked. */}
                  <div className="ph-mask flex-1 overflow-y-auto space-y-4 pr-1">
                    {/* User Identity Card */}
                    <div className={`rounded-v7-panel p-4 bg-v7-paper`}>
                      <div className="flex items-center space-x-4">
                        <div className={`w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0 bg-v7-paper text-v7-bronze-dark`}>
                          {(sidebarUser.name || sidebarUser.email).charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className={`text-lg font-medium truncate text-v7-ink`}>
                            {sidebarUser.name || "No Name"}
                          </h3>
                          <p className={`text-sm truncate text-v7-ink/60`}>
                            {sidebarUser.email}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <code className={`text-xs font-mono text-v7-ink/60`}>
                              {sidebarUser.id.substring(0, 12)}...
                            </code>
                            <button
                              onClick={() => handleCopyUserId(sidebarUser.id)}
                              className={`p-1 rounded-v7-control hover:bg-v7-mist text-v7-ink/60 hover:text-v7-ink-soft`}
                              title="Copy full user ID"
                            >
                              {copiedId ? (
                                <CheckCircle className="w-3 h-3 text-v7-success" />
                              ) : (
                                <Copy className="w-3 h-3" />
                              )}
                            </button>
                          </div>
                        </div>
                        {sidebarUser.emailVerified ? (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 bg-v7-success/10 text-v7-success`}>
                            Verified
                          </span>
                        ) : (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 bg-v7-paper text-v7-bronze-dark`}>
                            Pending
                          </span>
                        )}
                      </div>

                      {/* Role and Provider Row */}
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-v7-ink/15">
                        {sidebarUser.roles?.map(role => (
                          <span
                            key={role}
                            className={`px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(role)}`}
                          >
                            {role}
                          </span>
                        ))}
                        {sidebarUser.provider && (
                          <span className={`px-2 py-1 text-xs rounded-full bg-v7-paper text-v7-bronze-dark`}>
                            {sidebarUser.provider.name}
                          </span>
                        )}
                      </div>

                      <button type="button" onClick={() => setPermissionsUser(sidebarUser)} aria-haspopup="dialog" className={`${alphaAdminUi.link} mt-4`}>
                        Roles and scopes
                      </button>

                      {/* Timestamps */}
                      <div className="flex gap-4 mt-3 pt-3 border-t border-v7-ink/15">
                        <div className="flex items-center gap-1.5">
                          <Calendar className={`w-3.5 h-3.5 text-v7-ink/60`} />
                          <span className={`text-xs text-v7-ink/60`}>
                            Joined {new Date(sidebarUser.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Clock className={`w-3.5 h-3.5 text-v7-ink/60`} />
                          <span className={`text-xs text-v7-ink/60`}>
                            Updated {new Date(sidebarUser.updatedAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Advisor Account Widget - shown for ADVISOR role users */}
                    {sidebarUser.roles?.includes("ADVISOR") && (
                      <div className={`rounded-v7-panel border border-v7-ink/15 bg-v7-white`}>
                        <div className="flex items-center justify-between p-3 border-b border-v7-ink/15">
                          <div className="flex items-center gap-2">
                            <Users2 className={`w-4 h-4 text-v7-ink/60`} />
                            <h4 className={`text-sm font-medium text-v7-ink-soft`}>
                              Advisor Account
                            </h4>
                          </div>
                        </div>
                        <div className="p-3 space-y-3">
                          {advisorAccountLoading ? (
                            <div className="flex items-center gap-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-v7-ink/25"></div>
                              <span className={`text-sm text-v7-ink/60`}>Loading...</span>
                            </div>
                          ) : advisorAccountLinked ? (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className={`text-xs uppercase tracking-wider text-v7-ink/60`}>
                                  Linked Account
                                </span>
                                <code className={`text-sm font-mono text-v7-success`}>
                                  {advisorAccountLinked.accountId}
                                </code>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className={`text-xs uppercase tracking-wider text-v7-ink/60`}>
                                  Broker
                                </span>
                                <span className={`text-sm text-v7-ink-soft`}>
                                  {advisorAccountLinked.broker}
                                </span>
                              </div>
                              <button
                                onClick={handleUnlinkAdvisorAccount}
                                disabled={advisorLinkLoading}
                                className={`text-xs px-2 py-1 rounded-v7-control transition-colors text-v7-rust hover:bg-v7-rust/5 disabled:opacity-50`}
                              >
                                {advisorLinkLoading ? "..." : "Unlink"}
                              </button>
                            </div>
                          ) : (
                            <>
                              <p className={`text-sm text-v7-ink/60`}>
                                No advisor account linked. Enter the IBKR master account ID to link.
                              </p>
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={advisorAccountInput}
                                  onChange={(e) => setAdvisorAccountInput(e.target.value)}
                                  placeholder="e.g. F14359184"
                                  className={`flex-1 px-3 py-2 text-sm border rounded-v7-panel focus:outline-none focus:border-v7-bronze/30 focus:ring-1 focus:ring-v7-bronze-dark transition-all bg-v7-white border-v7-ink/15 text-v7-ink placeholder-v7-ink/45`}
                                />
                                <button
                                  onClick={handleLinkAdvisorAccount}
                                  disabled={advisorLinkLoading || !advisorAccountInput.trim()}
                                  className="px-3 py-2 bg-v7-ink hover:bg-v7-bronze-dark text-v7-white font-semibold rounded-v7-panel transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {advisorLinkLoading ? "..." : "Link"}
                                </button>
                              </div>
                              {advisorAccountError && (
                                <p className="text-sm text-v7-rust">{advisorAccountError}</p>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* IBKR Configuration Widget - hidden for advisor-only users */}
                    {!sidebarUser.roles?.includes("ADVISOR") && (<>
                    <UserIbkrConfigurationCard appearance="alpha"
                      isDarkMode={isDarkMode}
                      configuration={sidebarUserConfig}
                      isConfigurationLoading={sidebarConfigLoading}
                      schemaDescriptor={configurationSchema}
                      isSchemaLoading={configurationSchemaLoading}
                      onConfigure={() => openConfigModal(sidebarUser)}
                    />

                    <UserInvestorStrategyCard appearance="alpha"
                      isDarkMode={isDarkMode}
                      userId={sidebarUser.id}
                      sidebarStrategies={sidebarStrategies}
                      sidebarStrategiesLoading={sidebarStrategiesLoading}
                      sidebarStrategiesError={sidebarStrategiesError}
                      sidebarManagedCount={sidebarManagedCount}
                    />

                    {/* Strategy Analyze Widget */}
                    <div className={`rounded-v7-panel border border-v7-ink/15 bg-v7-white`}>
                      <div className="flex items-center justify-between p-3 border-b border-v7-ink/15">
                        <div className="flex items-center gap-2">
                          <BarChart3 className={`w-4 h-4 text-v7-ink/60`} />
                          <h4 className={`text-sm font-medium text-v7-ink-soft`}>
                            Strategy Analyze
                          </h4>
                        </div>
                        <a
                          href={`/admin/users/${sidebarUser.id}/analyze`}
                          aria-label="Open strategy analyzer"
                          className={`text-xs px-2 py-1 rounded-v7-control text-v7-bronze-dark hover:bg-v7-paper`}
                        >
                          Open
                        </a>
                      </div>
                      <div className="p-3">
                        {sidebarStrategiesLoading ? (
                          <div className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-v7-ink/25"></div>
                            <span className={`text-sm text-v7-ink/60`}>Loading...</span>
                          </div>
                        ) : sidebarStrategiesError ? (
                          <div className="text-sm text-v7-rust">
                            Failed to load strategy
                          </div>
                        ) : sidebarStrategies.length > 0 ? (
                          <div className="space-y-2">
                            <p className={`text-sm text-v7-ink-soft`}>
                              Like-for-like customer analyzer for risk, concentration, benchmark behavior, and product movement.
                            </p>
                            <div className="flex flex-wrap gap-2">
                              <span className={`px-2 py-0.5 rounded-full text-xs bg-v7-paper text-v7-ink-soft`}>
                                {sidebarStrategies.length} product{sidebarStrategies.length !== 1 ? "s" : ""}
                              </span>
                              {sidebarManagedCount > 0 && (
                                <span className={`px-2 py-0.5 rounded-full text-xs bg-v7-paper text-v7-ink-soft`}>
                                  {sidebarManagedCount} managed
                                </span>
                              )}
                              <span className="rounded-full border border-v7-bronze/30 bg-v7-bronze/10 px-2 py-0.5 text-xs text-v7-bronze-dark">
                                Admin view
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className={`text-sm italic text-v7-ink/60`}>
                            No strategy configured
                          </div>
                        )}
                      </div>
                    </div>

                    {isValidPlutusUserId(sidebarUser.id) && (
                      sidebarConfigLoading ? (
                        <div className={`rounded-v7-panel border border-v7-ink/15 bg-v7-white`}>
                          <div className="flex items-center justify-between p-3 border-b border-v7-ink/15">
                            <h4 className={`text-sm font-medium text-v7-ink-soft`}>
                              Customer View
                            </h4>
                          </div>
                          <div className="p-3">
                            <div className="h-8 w-24 animate-pulse rounded-v7-control bg-v7-mist" />
                          </div>
                        </div>
                      ) : sidebarUserConfig && sidebarUserConfig.userId !== sidebarUser.id ? (
                        null
                      ) : sidebarUserConfig
                        && sidebarUserConfig.userId === sidebarUser.id
                        && isCustomerViewEligible(
                          sidebarUser.roles ?? [],
                          sidebarUserConfig.data?.iscustomer
                        ) ? (
                        <div className={`rounded-v7-panel border border-v7-ink/15 bg-v7-white`}>
                          <div className="flex items-center justify-between p-3 border-b border-v7-ink/15">
                            <h4 className={`text-sm font-medium text-v7-ink-soft`}>
                              Customer View
                            </h4>
                            <button
                              type="button"
                              onClick={() => {
                                if (!actor?.id) {
                                  toast.error("Customer View could not be opened in this tab.");
                                  return;
                                }
                                const mapping = createAdminCustomerViewRouteMapping(
                                  actor.id,
                                  sidebarUser.id
                                );
                                if (!mapping) {
                                  toast.error("Customer View could not be opened in this tab.");
                                  return;
                                }
                                window.location.assign(
                                  `/admin/customer-view/${mapping.routeSubjectKey}/dashboard`
                                );
                              }}
                              className={`text-xs px-2 py-1 rounded-v7-control text-v7-bronze-dark hover:bg-v7-paper`}
                            >
                              Open
                            </button>
                          </div>
                          <div className="p-3">
                            <p className={`text-sm text-v7-ink-soft`}>
                              Read-only Alpha Dashboard, Positions, and Profile for this customer.
                            </p>
                          </div>
                        </div>
                      ) : null
                    )}

                    {/* Account Summary Widget */}
                    <AdminAccountSummary appearance="alpha" key={sidebarUser.id} userId={sidebarUser.id} userName={sidebarUser.name || sidebarUser.email} />
                    </>)}

                    {/* Contract Exclusions Widget */}
                    <div className={`rounded-v7-panel border border-v7-ink/15 bg-v7-white`}>
                      <div className="flex items-center justify-between p-3 border-b border-v7-ink/15">
                        <div className="flex items-center gap-2">
                          <ShieldOff className={`w-4 h-4 text-v7-ink/60`} />
                          <h4 className={`text-sm font-medium text-v7-ink-soft`}>
                            Contract Exclusions
                          </h4>
                          {!sidebarExclusionsLoading && sidebarExclusions.length > 0 && (
                            <span className={`px-1.5 py-0.5 text-xs rounded-full bg-v7-paper text-v7-bronze-dark`}>
                              {sidebarExclusions.length}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => openExclusionsModal(sidebarUser)}
                          className={`text-xs px-2 py-1 rounded-v7-control text-v7-bronze-dark hover:bg-v7-paper`}
                        >
                          Manage
                        </button>
                      </div>
                      <div className="p-3">
                        {sidebarExclusionsLoading ? (
                          <div className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-v7-ink/25"></div>
                            <span className={`text-sm text-v7-ink/60`}>Loading...</span>
                          </div>
                        ) : sidebarExclusions.length > 0 ? (
                          <div className="space-y-1.5">
                            {sidebarExclusions.slice(0, 3).map((excl) => (
                              <div 
                                key={excl.id} 
                                className={`flex items-center justify-between text-sm p-1.5 rounded-v7-control bg-v7-paper`}
                              >
                                <code className={`font-mono text-xs text-v7-ink-soft`}>
                                  {excl.conId}
                                </code>
                                {excl.message && (
                                  <span className={`text-xs truncate max-w-[150px] text-v7-ink/60`}>
                                    {excl.message}
                                  </span>
                                )}
                              </div>
                            ))}
                            {sidebarExclusions.length > 3 && (
                              <div className={`text-xs text-v7-ink/60`}>
                                +{sidebarExclusions.length - 3} more exclusions
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className={`text-sm italic text-v7-ink/60`}>
                            No contract exclusions
                          </div>
                        )}
                      </div>
                    </div>

                    <UserAgreementsCard appearance="alpha"
                      isDarkMode={isDarkMode}
                      sidebarAgreements={sidebarAgreements}
                      sidebarAgreementsLoading={sidebarAgreementsLoading}
                    />

                    {/* Questionnaire Q&A Widget */}
                    <div className={`rounded-v7-panel border border-v7-ink/15 bg-v7-white`}>
                      <div className="flex items-center justify-between p-3 border-b border-v7-ink/15">
                        <div className="flex items-center gap-2">
                          <HelpCircle className={`w-4 h-4 text-v7-ink/60`} />
                          <h4 className={`text-sm font-medium text-v7-ink-soft`}>
                            Questionnaire Q&A
                          </h4>
                          {!sidebarFormResponsesLoading && sidebarFormResponses.length > 0 && (
                            <span className={`px-1.5 py-0.5 text-xs rounded-full bg-v7-paper text-v7-bronze-dark`}>
                              {sidebarFormResponses.length}
                            </span>
                          )}
                        </div>
                        <a
                          href="/admin/questions"
                          className={`text-xs px-2 py-1 rounded-v7-control flex items-center gap-1 text-v7-bronze-dark hover:bg-v7-paper`}
                        >
                          Manage
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                      <div className="p-3">
                        {sidebarFormResponsesLoading ? (
                          <div className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-v7-ink/25"></div>
                            <span className={`text-sm text-v7-ink/60`}>Loading...</span>
                          </div>
                        ) : sidebarFormResponses.length > 0 ? (
                          <div className="space-y-2">
                            {[...sidebarFormResponses]
                              .sort((a, b) => {
                                const qA = sidebarFormQuestions?.find(q => q.id === a.questionId);
                                const qB = sidebarFormQuestions?.find(q => q.id === b.questionId);
                                const orderA = qA?.order ?? a.questionId;
                                const orderB = qB?.order ?? b.questionId;
                                return orderA - orderB;
                              })
                              .map((r) => {
                                const question = sidebarFormQuestions?.find(q => q.id === r.questionId);
                                const label = question?.question ?? `Question #${r.questionId}`;
                                return (
                                  <div
                                    key={r.id}
                                    className={`p-2 rounded-v7-control bg-v7-paper`}
                                  >
                                    <div className={`text-xs font-medium text-v7-ink/60`}>
                                      {label}
                                    </div>
                                    <div className={`text-sm mt-0.5 text-v7-ink`}>
                                      {r.answer || "-"}
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        ) : (
                          <div className={`text-sm italic text-v7-ink/60`}>
                            No questionnaire answers
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Notification contacts (PWA-2690) */}
                    <UserNotificationContactsCard
                      key={sidebarUser.id}
                      userId={sidebarUser.id}
                      loginEmail={sidebarUser.email}
                      isDarkMode={isDarkMode}
                    />

                    {/* Quick Actions */}
                    <div className={`rounded-v7-panel border border-v7-ink/15 bg-v7-white`}>
                      <div className="p-3 border-b border-v7-ink/15">
                        <h4 className={`text-sm font-medium text-v7-ink-soft`}>
                          Quick Actions
                        </h4>
                      </div>
                      <div className="p-3 space-y-2">
                        <button
                          onClick={() => openEditModal(sidebarUser)}
                          className="w-full flex items-center justify-center px-3 py-2 border border-transparent rounded-v7-control text-sm font-semibold text-v7-white bg-v7-ink hover:bg-v7-bronze-dark"
                        >
                          <Edit3 className="w-4 h-4 mr-2" />
                          Edit User
                        </button>
                        
                        <button
                          onClick={() => openDeleteConfirmation(sidebarUser)}
                          className={`w-full flex items-center justify-center px-3 py-2 border rounded-v7-control text-sm font-medium border-v7-rust/30 text-v7-rust hover:bg-v7-rust/5`}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete User
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null
            }
            mainContent={
              <div className="flex min-h-full flex-col pr-0 md:pr-5">
                {/* Controls */}
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3 flex-shrink-0">
                  {/* Search and Filters */}
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Search Column Selector */}
                    <select
                      aria-label="Search column"
                      value={search.searchColumn}
                      onChange={(e) => search.setSearchColumn(e.target.value as any)}
                      className={`border rounded-v7-control px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-v7-bronze-dark bg-v7-white border-v7-ink/25 text-v7-ink`}
                    >
                      <option value="">All Columns</option>
                      <option value="id">User ID</option>
                      <option value="name">Name</option>
                      <option value="email">Email</option>
                      <option value="providerName">Provider</option>
                    </select>

                    {/* Search Input */}
                    <div className="relative flex-1 min-w-[200px]">
                      <Search
                        className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-v7-ink/60`}
                      />
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={search.searchQuery}
                        onChange={(e) => search.setSearchQuery(e.target.value)}
                        placeholder={getSearchPlaceholder()}
                        className={`w-full pl-10 ${search.searchQuery ? "pr-10" : "pr-4"} py-2 text-sm border rounded-v7-control focus:outline-none focus:ring-2 focus:ring-v7-bronze-dark bg-v7-white border-v7-ink/25 text-v7-ink placeholder-v7-ink/45`}
                      />
                      {search.searchQuery && (
                        <button
                          type="button"
                          aria-label="Clear search"
                          onClick={() => {
                            search.setSearchQuery("");
                            searchInputRef.current?.focus();
                          }}
                          className={`absolute right-2 top-1/2 -translate-y-1/2 rounded-v7-control p-1 transition-colors focus:outline-none focus:ring-2 focus:ring-v7-bronze-dark text-v7-ink/60 hover:bg-v7-paper hover:text-v7-ink-soft`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* Role Filter - Multi-select Dropdown */}
                    <div className="relative" ref={roleDropdownRef}>
                      <button
                        type="button"
                        onClick={() => setRoleDropdownOpen(!roleDropdownOpen)}
                        className={`border rounded-v7-control px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-v7-bronze-dark flex items-center gap-2 min-w-[140px] bg-v7-white border-v7-ink/25 text-v7-ink`}
                      >
                        <span>
                          {filters.roleFilters.size === 0
                            ? "All Roles"
                            : `${filters.roleFilters.size} role(s)`}
                        </span>
                        <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${roleDropdownOpen ? "rotate-180" : ""}`} />
                      </button>

                      {roleDropdownOpen && (
                        <div className={`absolute z-20 mt-1 w-48 border rounded-v7-control shadow-lg bg-v7-white border-v7-ink/25`}>
                          {(["ADMIN", "PROVIDER", "ADVISOR", "INVESTOR"] as const).map((role) => (
                            <label
                              key={role}
                              className={`flex items-center px-3 py-2 cursor-pointer hover:bg-v7-paper text-v7-ink`}
                            >
                              <input
                                type="checkbox"
                                checked={filters.roleFilters.has(role)}
                                onChange={() => filters.toggleRoleFilter(role)}
                                className="mr-2"
                              />
                              {role}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    {/* Page Size Selector */}
                    <div className="flex items-center gap-2">
                      <label className={`text-xs text-v7-ink-soft`}>Rows per page</label>
                      <select
                        id="users-page-size"
                        value={pagination.pageSize}
                        onChange={(e) => pagination.setPageSize(Number(e.target.value))}
                        className={`px-2 py-1 text-sm border rounded-v7-control bg-v7-white border-v7-ink/25 text-v7-ink`}
                      >
                        <option value={10}>10</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                        <option value={500}>500</option>
                      </select>
                    </div>
                    
                    {/* Download CSV Button */}
                    <button
                      onClick={downloadCSV}
                      disabled={downloading || users.length === 0}
                      className={alphaAdminUi.secondary}
                      title="Download current filtered users as CSV"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {downloading ? "Preparing..." : "Download CSV"}
                    </button>
                  </div>
                </div>

                {/* Users Table */}
                <div className={`bg-v7-white shadow-sm rounded-v7-panel flex-1 overflow-hidden flex flex-col`}>
                  <div className={`px-6 py-4 border-b flex-shrink-0 border-v7-ink/15`}>
                    <h3 className={`text-lg font-medium text-v7-ink`}>All Users</h3>
                  </div>
                  <div className="flex-1 overflow-auto">
                    <table className={`min-w-full divide-y divide-v7-ink/15`}>
                      <thead className={`bg-v7-paper sticky top-0 z-10`}>
                        <tr>
                          <th
                            className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-v7-ink/60 cursor-pointer select-none`}
                            onClick={() => sorting.handleSort("id")}
                            title="Sort by user ID"
                          >
                            User ID{sortIndicator("id")}
                          </th>
                          <th
                            className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-v7-ink/60 cursor-pointer select-none`}
                            onClick={() => sorting.handleSort("name")}
                            title="Sort by user name"
                          >
                            User{sortIndicator("name")}
                          </th>
                          <th
                            className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-v7-ink/60 cursor-pointer select-none`}
                            onClick={() => sorting.handleSort("roleName")}
                            title="Sort by role"
                          >
                            Role{sortIndicator("roleName")}
                          </th>
                          <th
                            className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-v7-ink/60 cursor-pointer select-none`}
                            onClick={() => sorting.handleSort("providerName")}
                            title="Sort by provider"
                          >
                            Provider{sortIndicator("providerName")}
                          </th>
                          <th
                            className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-v7-ink/60 cursor-pointer select-none`}
                            onClick={() => sorting.handleSort("emailVerified")}
                            title="Sort by verification status"
                          >
                            Status{sortIndicator("emailVerified")}
                          </th>
                          <th
                            className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-v7-ink/60 cursor-pointer select-none`}
                            onClick={() => sorting.handleSort("createdAt")}
                            title="Sort by created date"
                          >
                            Created{sortIndicator("createdAt")}
                          </th>
                          {productNotificationsEnabled && <th scope="col" className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-v7-ink/60">Product notifications</th>}
                        </tr>
                      </thead>
                      {/* ph-mask: user list rows contain emails, names, and
                          provider IDs. Mask the entire tbody so replays do
                          not capture PII. */}
                      <tbody className={`ph-mask bg-v7-white divide-v7-ink/15 divide-y`}>
                        {users.map(user => (
                          <SelectableTableRow appearance="alpha"
                            key={user.id}
                            isSelected={sidebarUser?.id === user.id}
                            onClick={() => handleSidebarUserSelect(user)}
                          >
                            <td
                              className={`px-6 py-4 whitespace-nowrap text-xs font-mono text-v7-ink/60`}
                            >
                              <div className="flex items-center gap-2">
                                <span title={user.id}>{user.id.substring(0, 8)}...</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(user.id);
                                  }}
                                  className={`p-1 rounded-v7-control text-v7-ink/60 hover:text-v7-ink-soft hover:bg-v7-paper`}
                                  title="Copy user ID"
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10">
                                  <div
                                    className={`h-10 w-10 rounded-full flex items-center justify-center bg-v7-mist`}
                                  >
                                    <span className={`text-sm font-medium text-v7-ink-soft`}>
                                      {user.name ? user.name.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                </div>
                                <div className="ml-4">
                                  <div className={`text-sm font-medium text-v7-ink`}>
                                    {user.name || "No name"}
                                  </div>
                                  <div className={`text-sm text-v7-ink/60`}>{user.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex flex-wrap gap-1">
                                {user.roles && user.roles.length > 0 ? (
                                  user.roles.map((role, index) => (
                                    <span
                                      key={index}
                                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(role)}`}
                                    >
                                      {role}
                                    </span>
                                  ))
                                ) : (
                                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-v7-paper text-v7-ink">
                                    No role
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm text-v7-ink`}>
                              {user.provider ? user.provider.name : "Not assigned"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.emailVerified
    ? "bg-v7-success/10 text-v7-success" : "bg-v7-paper text-v7-bronze-dark"}`}
                              >
                                {user.emailVerified ? "Verified" : "Unverified"}
                              </span>
                            </td>
                            <td
                              className={`px-6 py-4 whitespace-nowrap text-sm text-v7-ink/60`}
                            >
                              {new Date(user.createdAt).toLocaleDateString()}
                            </td>
                            {productNotificationsEnabled && <td className="px-6 py-4 text-right whitespace-nowrap">
                              <button type="button" className={alphaAdminUi.secondary} aria-label={`Product notifications for ${user.email}`}
                                onClick={event => { event.stopPropagation(); setProductNotificationsUser(user); }}>
                                <Bell className="h-4 w-4" aria-hidden="true" />Product notifications
                              </button>
                            </td>}
                          </SelectableTableRow>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

        {/* Pagination */}
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className={`text-v7-ink-soft text-sm`}>
            Showing {pagination.totalItems === 0 ? 0 : startIndex}-{endIndex} of {pagination.totalItems}
          </div>
          <div className="flex items-center gap-2">
            <button
              className={`px-2 py-1 text-sm rounded-v7-control border border-v7-ink/25 text-v7-ink-soft hover:bg-v7-paper disabled:opacity-50`}
              onClick={() => pagination.setCurrentPage(1)}
              disabled={pagination.currentPage === 1}
            >
              First
            </button>
            <button
              className={`px-2 py-1 text-sm rounded-v7-control border border-v7-ink/25 text-v7-ink-soft hover:bg-v7-paper disabled:opacity-50`}
              onClick={() => pagination.setCurrentPage(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
            >
              Prev
            </button>
            <span className={`text-v7-ink-soft text-sm`}>
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
            <button
              className={`px-2 py-1 text-sm rounded-v7-control border border-v7-ink/25 text-v7-ink-soft hover:bg-v7-paper disabled:opacity-50`}
              onClick={() => pagination.setCurrentPage(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
            >
              Next
            </button>
            <button
              className={`px-2 py-1 text-sm rounded-v7-control border border-v7-ink/25 text-v7-ink-soft hover:bg-v7-paper disabled:opacity-50`}
              onClick={() => pagination.setCurrentPage(pagination.totalPages)}
              disabled={pagination.currentPage === pagination.totalPages}
            >
              Last
            </button>
          </div>
        </div>
      </div>
    }
          />
      </div>

      {/* User Management Modal */}
      {productNotificationsEnabled && productNotificationsUser && <UserProductNotificationMappingsModal
        user={productNotificationsUser} onClose={() => setProductNotificationsUser(null)}
      />}
      <UserModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        user={selectedUser}
        mode={modalMode}
        onSave={modalMode === "create" ? handleCreateUser : data => selectedUser ? saveUser(selectedUser, data) : Promise.resolve()}
        onManagePermissions={selectedUser ? () => { setModalOpen(false); setPermissionsUser(selectedUser); } : undefined}
      />

      {permissionsUser && <UserPermissionsModal isOpen user={permissionsUser}
        onClose={() => setPermissionsUser(null)} onSave={data => saveUser(permissionsUser, data)} />}

      {/* User Configuration Modal */}
      <UserConfigurationModal appearance="alpha"
        isOpen={configModalOpen}
        onClose={() => setConfigModalOpen(false)}
        userId={configUserId}
        userName={configUserName}
        configurationSchemaDescriptor={configurationSchema}
        onSave={() => {
          if (configUserId && currentSidebarUserIdRef.current === configUserId) {
            fetchSidebarUserData(configUserId);
          }
        }}
      />

      {/* Contract Exclusions Modal */}
      <ContractExclusionsModal appearance="alpha"
        isOpen={exclusionsModalOpen}
        onClose={() => setExclusionsModalOpen(false)}
        userId={exclusionsUserId}
        userName={exclusionsUserName}
      />

      {deleteConfirmOpen && userToDelete && <AccountDeletionDialog appearance="alpha"
        key={`${actor?.id}:${userToDelete.id}`}
        targetUserId={userToDelete.id}
        accountLabel={userToDelete.email}
        onClose={() => { setDeleteConfirmOpen(false); setUserToDelete(null); }}
        onDeleted={async result => {
          setDeleteConfirmOpen(false);
          setUserToDelete(null);
          if (result.logoutActor) {
            await logout();
            return;
          }
          if (sidebarUser?.id === result.targetUserId) setSidebarUser(null);
          toast.success("Account deleted. An administrator can restore it from Deleted users.");
          await userActions.fetchUsers();
        }}
      />}

      {/* Provider roster sync modal */}
      <BulkAddUsersModal appearance="alpha"
        isOpen={bulkAddModalOpen}
        onClose={() => setBulkAddModalOpen(false)}
        onSuccess={() => userActions.fetchUsers()}
      />

      {/* Non-Lowercase Emails Bulk Delete Modal */}
      {nonLowercaseModalOpen && (
        <AlphaSettingsModal isOpen onClose={() => { setNonLowercaseModalOpen(false); setNonLowercaseUsers([]); }}
          title="Non-Lowercase Emails" closeDisabled={bulkDeleting} maxWidthClass="max-w-lg" spacious>
            {/* Content */}
            <div className="p-6">
              {nonLowercaseUsers.length === 0 ? (
                <p className={`text-center text-v7-ink-soft`}>
                  All user emails are already lowercase. Nothing to delete.
                </p>
              ) : (
                <>
                  <p className={`mb-4 text-v7-ink-soft`}>
                    Found <span className="font-bold text-v7-rust">{nonLowercaseUsers.length}</span> user(s) with non-lowercase emails. Their sessions will end immediately. An administrator can restore them from Deleted users. Deleting a managed account also signs you out and stops this batch.
                  </p>

                  {/* Progress bar during deletion */}
                  {bulkDeleting && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm text-v7-ink/60`}>
                          Deleting... {bulkDeleteProgress.completed} / {bulkDeleteProgress.total}
                        </span>
                        {bulkDeleteProgress.failed.length > 0 && (
                          <span className="text-sm text-v7-rust">
                            {bulkDeleteProgress.failed.length} failed
                          </span>
                        )}
                      </div>
                      <div className={`w-full h-2 rounded-full bg-v7-mist`}>
                        <div
                          className="h-2 rounded-full bg-v7-rust/5 transition-all duration-300"
                          style={{ width: `${bulkDeleteProgress.total > 0 ? (bulkDeleteProgress.completed / bulkDeleteProgress.total) * 100 : 0}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Scrollable user list */}
                  <div className={`max-h-64 overflow-y-auto border rounded-v7-panel border-v7-ink/15`}>
                    <table className={`min-w-full divide-y divide-v7-ink/15`}>
                      <thead className={`bg-v7-paper sticky top-0`}>
                        <tr>
                          <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-v7-ink/60`}>
                            Email
                          </th>
                          <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-v7-ink/60`}>
                            Name
                          </th>
                          <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-v7-ink/60`}>
                            Roles
                          </th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y divide-v7-ink/15`}>
                        {nonLowercaseUsers.map((u) => (
                          <tr key={u.id} className="bg-v7-white">
                            <td className={`px-4 py-2 text-sm font-mono text-v7-ink-soft`}>
                              {u.email}
                            </td>
                            <td className={`px-4 py-2 text-sm text-v7-ink-soft`}>
                              {u.name || "-"}
                            </td>
                            <td className={`px-4 py-2 text-sm text-v7-ink-soft`}>
                              {(u.roles || []).join(", ") || "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className={`flex items-center justify-end p-6 border-t space-x-3 border-v7-ink/15`}>
              <button
                type="button"
                onClick={() => {
                  if (!bulkDeleting) {
                    setNonLowercaseModalOpen(false);
                    setNonLowercaseUsers([]);
                  }
                }}
                disabled={bulkDeleting}
                className={`px-4 py-2 rounded-v7-panel transition-colors disabled:opacity-50 text-v7-ink-soft hover:text-v7-ink-soft hover:bg-v7-paper`}
              >
                Cancel
              </button>
              {nonLowercaseUsers.length > 0 && (
                <button
                  type="button"
                  onClick={handleBulkDeleteNonLowercase}
                  disabled={bulkDeleting}
                  className="px-4 py-2 text-v7-white rounded-v7-panel transition-colors disabled:opacity-50 bg-v7-rust hover:bg-v7-rust"
                >
                  {bulkDeleting
                    ? `Deleting ${bulkDeleteProgress.completed}/${bulkDeleteProgress.total}...`
                    : `Delete ${nonLowercaseUsers.length} User(s)`}
                </button>
              )}
            </div>
        </AlphaSettingsModal>
      )}
    </div>
  );
};

const USER_TABS = [
  { id: "all", label: "All Users" },
  { id: "deleted", label: "Deleted users" },
  { id: "stats", label: "Stats" },
] as const;
type UserTab = typeof USER_TABS[number]["id"];

const UsersPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<UserTab>("all");
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const tabPrefix = React.useId();

  const handleTabKey = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number;
    switch (event.key) {
      case "ArrowRight": nextIndex = (index + 1) % USER_TABS.length; break;
      case "ArrowLeft": nextIndex = (index + USER_TABS.length - 1) % USER_TABS.length; break;
      case "Home": nextIndex = 0; break;
      case "End": nextIndex = USER_TABS.length - 1; break;
      default: return;
    }
    event.preventDefault();
    setActiveTab(USER_TABS[nextIndex].id);
    tabRefs.current[nextIndex]?.focus();
  };

  return (
    <AdminPageLayout appearance="alpha" title="Users" description="Manage profiles, access, and account settings."
      hideContentScroll={activeTab === "all"}
      headerContent={
        <div role="tablist" aria-label="Users" className="mt-5 inline-flex max-w-full gap-1 overflow-x-auto rounded-v7-control border border-v7-ink/15 bg-v7-white p-1">
          {USER_TABS.map((tab, index) => (
            <button key={tab.id} ref={element => { tabRefs.current[index] = element; }} type="button" role="tab"
              id={`${tabPrefix}-tab-${tab.id}`} aria-controls={`${tabPrefix}-panel-${tab.id}`}
              aria-selected={activeTab === tab.id} tabIndex={activeTab === tab.id ? 0 : -1}
              onClick={() => setActiveTab(tab.id)} onKeyDown={event => handleTabKey(event, index)}
              className={`whitespace-nowrap rounded-v7-control px-4 py-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-v7-bronze-dark ${activeTab === tab.id ? "bg-v7-ink text-v7-white" : "text-v7-ink/60 hover:text-v7-ink-soft"}`}>
              {tab.label}
            </button>
          ))}
        </div>
      }>
      <div key={`${user?.id}:${activeTab}`} role="tabpanel" id={`${tabPrefix}-panel-${activeTab}`}
        aria-labelledby={`${tabPrefix}-tab-${activeTab}`} tabIndex={0} className="h-full outline-none focus-visible:ring-2 focus-visible:ring-v7-bronze-dark">
        {activeTab === "all" ? <AllUsersTab /> : activeTab === "deleted" ? <DeletedUsersTab /> : <UserActivityPanel initiallyExpanded />}
      </div>
    </AdminPageLayout>
  );
};

export default UsersPage;
